import { ActionIcon, Group, Title, Tooltip } from "@mantine/core";
import { ReactNode } from "react";
import { IconBrandGithub } from "../icons";
import { currentAsOf } from "./build-date";
import { InfoMark } from "./info-mark";
import { PageWidthToggle } from "./page-width";

export function UtilityTitle({ directory, publications, control, children }: UtilityTitleProps) {
  return (
    <Group className="utility-title" justify="space-between" align="center" wrap="nowrap" gap="sm">
      <Group gap={4} align="center" wrap="nowrap">
        {/* The page's one <h1>, sized as an h2; Mantine's 1.35 leading is trimmed so centring lands on the glyphs. */}
        <Title order={1} size="h2" lh={1.15}>{children}</Title>
        <Tooltip label="View source on GitHub" withArrow>
          <ActionIcon
            component="a"
            href={`${SOURCE_BASE}/${directory}`}
            target="_blank"
            rel="noopener noreferrer"
            variant="subtle"
            color="gray"
            size="md"
            aria-label={`View the ${children} source on GitHub`}
          >
            {/* Well under the cap height: a source link reads as a secondary mark beside the title, not part of it. */}
            <IconBrandGithub size="1.25rem" stroke={1.5} />
          </ActionIcon>
        </Tooltip>
        {publications !== undefined && <InfoMark label={currentAsOf(publications)} />}
      </Group>
      {/* The end of the row is the row's and not the page's, so the width toggle is last on every one of them. */}
      <Group gap="sm" align="center" wrap="nowrap">
        {control}
        <PageWidthToggle />
      </Group>
    </Group>
  );
}

interface UtilityTitleProps {
  // Each page names its own directory: after bundling nothing on the page still knows which module it came from, and
  // the directory is the whole utility where the page file alone would be the components and none of the work.
  directory: string;
  // Who published what the page reads, on the four pages reading something that was downloaded rather than computed;
  // absent on the rest, whose answers are arithmetic and cannot go stale.
  publications?: string;
  // The one control a page draws beside its title — a mode switch on the eight that have one, and nothing on the rest.
  control?: ReactNode;
  children: string;
}

// A tree URL rather than a blob one, since what this points at is a directory.
const SOURCE_BASE = "https://github.com/manualpilot/utils.plus/tree/main/src/utilities";
